import { cssVariables, isDefined } from "@/utils/utils";
import { Col, Divider, FormInstance, Row, Statistic } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import t from "../../../../../app/i18n";
import { useGetVehicleBrand, useGetVehicleModel } from "../../../../../common/api/queries/enumerations";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import {
  formatCoverageLimitValues,
  formatLocaleCurrency,
  formatLocaleDate,
  formatPhoneNumber,
} from "../../../../../common/utils/formatUtils";
import { licensePlateNormalizeFunction } from "../../../../../common/utils/formUtils";
import { ClientsListView } from "../../../../client/components/ClientsListView";
import { ClientFormType, ClientType } from "../../../../client/enums";
import { Client, NaturalClient, SelfEmployedClient } from "../../../../client/types";
import { createClientsData } from "../../../../client/utils";
import { PaymentFrequency } from "../../../../contract/enums";
import { parseVehicleModelName } from "../../../../contract/utils";
import { InstitutionEnum } from "../../../../institution/enums";
import { CalcType } from "../../../enums";
import { CalcResult, GenGainersData } from "../../types";
import { DEFEND_D2C_COVERAGE } from "../../utils";
import { GapCoverageLimit, GapDuration, VehicleOwnerRelation, VehiclePolicyHolderRelation } from "../../vehicle/enums";
import type {
  CrashCalcResultData,
  MtplCalcResultData,
  MtplCrashCalcResultData,
  PasCalcResultData,
  VehicleCalcResultData,
  VehicleCalcVehicleData,
  VehicleFormClients,
} from "../../vehicle/types";
import { isGapCalcResult } from "../../vehicle/utils";
import type { VehicleCalcSummaryViewData } from "./CalcSummaryModal";
import { GainersCommissionRateSection } from "./GainersCommissionRateSection";

const SPAN_LABEL = 5;
const SPAN_VALUE = 19;

const getOwnerClient = (ownerRelation: VehicleOwnerRelation, clients: VehicleFormClients) => {
  if (ownerRelation === VehicleOwnerRelation.SAME_AS_VEHICLE_HOLDER) {
    return clients.holder;
  } else if (ownerRelation === VehicleOwnerRelation.SAME_AS_POLICY_HOLDER) {
    return clients.policyHolder;
  } else {
    return clients.owner;
  }
};

const renderVehicleInsuranceData = (
  selectedInsurance: CalcResult<VehicleCalcResultData>,
  gapData: { gapDuration?: GapDuration; gapComplicityReinsurance?: boolean; gapCoverageLimit?: GapCoverageLimit },
): React.JSX.Element | undefined => {
  const SPAN_LABEL = 11;
  const SPAN_VALUE = 13;

  switch (selectedInsurance.calcType) {
    case CalcType.MTPL:
      const mtplInsurance = { ...selectedInsurance } as CalcResult<MtplCalcResultData>;
      return (
        <>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.results.coverageLimit")}</Col>
            <Col span={SPAN_VALUE}>
              {mtplInsurance.data &&
              !!mtplInsurance.data.healthCoverageLimit &&
              !!mtplInsurance.data.propertyCoverageLimit
                ? formatCoverageLimitValues(
                    mtplInsurance.data.healthCoverageLimit,
                    mtplInsurance.data.propertyCoverageLimit,
                  )
                : undefined}
            </Col>
          </Row>
          <Row>
            {mtplInsurance.data?.appliedCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage.type}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage.type)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  </Col>
                </React.Fragment>
              );
            })}
            {mtplInsurance.data?.missingCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </>
      );
    case CalcType.GAP:
      return (
        <>
          {gapData.gapDuration ? (
            <Row>
              <Col span={SPAN_LABEL}>{t("calc.vehicle.enums.gapDuration._label")}</Col>
              <Col span={SPAN_VALUE}>{t("calc.vehicle.enums.gapDuration." + gapData.gapDuration)}</Col>
            </Row>
          ) : undefined}

          {selectedInsurance.insuranceInstitution.institutionEnum === InstitutionEnum.DEFEND ? (
            <Row>
              <Col span={SPAN_LABEL}>{t("calc.vehicle.results.coverageLimit")}</Col>
              <Col span={SPAN_VALUE}>{t("calc.vehicle.enums.gapCoverageLimit." + gapData.gapCoverageLimit)}</Col>
            </Row>
          ) : undefined}

          {selectedInsurance.coverage !== DEFEND_D2C_COVERAGE ? (
            <Row>
              <Col span={SPAN_LABEL}>{t("calc.vehicle.attrs.generalData.gapComplicityReinsurance")}</Col>
              <Col span={SPAN_VALUE}>
                {gapData.gapComplicityReinsurance ? (
                  <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                ) : (
                  <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                )}
              </Col>
            </Row>
          ) : undefined}
        </>
      );
    case CalcType.PAS:
      const pasInsurance = { ...selectedInsurance } as CalcResult<PasCalcResultData>;
      return (
        <>
          <Row>
            {pasInsurance.data?.coverages?.map((coverage) => {
              return (
                <React.Fragment key={coverage.name}>
                  <Col span={SPAN_LABEL}>{coverage.name}</Col>
                  <Col span={SPAN_VALUE}>{coverage.limit}</Col>
                </React.Fragment>
              );
            })}
          </Row>
        </>
      );
    case CalcType.CRASH:
      const crashInsurance = { ...selectedInsurance } as CalcResult<CrashCalcResultData>;
      return (
        <>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.results.complicity")}</Col>
            <Col span={SPAN_VALUE}>{crashInsurance.data?.complicity}</Col>
          </Row>
          <Row>
            {crashInsurance.data?.appliedCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage.type}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage.type)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  </Col>
                </React.Fragment>
              );
            })}
            {crashInsurance.data?.missingCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </>
      );
    case CalcType.MTPL_CRASH:
      const mtplCrashInsurance = { ...selectedInsurance } as CalcResult<MtplCrashCalcResultData>;
      return (
        <>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.results.coverageLimit")}</Col>
            <Col span={SPAN_VALUE}>
              {mtplCrashInsurance.data &&
              !!mtplCrashInsurance.data.healthCoverageLimit &&
              !!mtplCrashInsurance.data.propertyCoverageLimit
                ? formatCoverageLimitValues(
                    mtplCrashInsurance.data.healthCoverageLimit,
                    mtplCrashInsurance.data.propertyCoverageLimit,
                  )
                : undefined}
            </Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.results.complicity")}</Col>
            <Col span={SPAN_VALUE}>{mtplCrashInsurance.data?.complicity}</Col>
          </Row>
          <Row>
            {mtplCrashInsurance.data?.appliedCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage.type}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage.type)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  </Col>
                </React.Fragment>
              );
            })}
            {mtplCrashInsurance.data?.missingCoverages.map((coverage) => {
              return (
                <React.Fragment key={coverage}>
                  <Col span={SPAN_LABEL}>{t("contract.enums.insuranceCoverageType." + coverage)}</Col>
                  <Col span={SPAN_VALUE}>
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  </Col>
                </React.Fragment>
              );
            })}
          </Row>
        </>
      );
    default:
      return undefined;
  }
};

const renderVehicleData = (vehicleData: VehicleCalcVehicleData, brandName: string, modelName?: string) => {
  return (
    <>
      <Row>
        <Col span={SPAN_LABEL}>{t("calc.vehicle.helpers.vehicleBrandAndModel")}:</Col>
        <Col span={SPAN_VALUE}>
          {parseVehicleModelName({
            customModel: vehicleData.customModel,
            customBrand: vehicleData.customBrand,
            brandName: brandName,
            modelName: modelName ?? "",
          })}
        </Col>
      </Row>
      <Row>
        <Col span={SPAN_LABEL}>{t("calc.vehicle.attrs.vehicleData.licensePlate")}</Col>
        <Col span={SPAN_VALUE}>
          {vehicleData.licensePlate ? licensePlateNormalizeFunction(vehicleData.licensePlate) : undefined}
        </Col>
      </Row>
      <Row>
        <Col span={SPAN_LABEL}>{t("calc.vehicle.attrs.vehicleData.vin")}</Col>
        <Col span={SPAN_VALUE}>{vehicleData.vin}</Col>
      </Row>
    </>
  );
};

type Props = {
  data: VehicleCalcSummaryViewData;
  divideCommission: boolean;
  setDivideCommission: (value: boolean) => void;
  gainersDataForm: FormInstance<GenGainersData>;
};

const VehicleCalcSummaryView = ({
  data: { selectedCalcResult, formData, calcData, clients },
  gainersDataForm,
  setDivideCommission,
  divideCommission,
}: Props) => {
  const { t } = useTranslation();

  const holderIdentityCard =
    clients.holder?.type === ClientType.NATURAL || clients.holder?.type === ClientType.SELF_EMPLOYED
      ? formData.clientsData.holderIdentityCardNumber
      : undefined;

  const holderClient = holderIdentityCard
    ? { ...(clients.holder as NaturalClient | SelfEmployedClient), identityCardNumber: holderIdentityCard }
    : clients.holder;

  const policyHolderClient =
    calcData.clientsData.policyHolderRelation === VehiclePolicyHolderRelation.SAME_AS_VEHICLE_HOLDER
      ? holderClient
      : clients.policyHolder;

  const ownerClient = getOwnerClient(calcData.clientsData.ownerRelation, {
    ...clients,
    holder: holderClient,
    policyHolder: policyHolderClient,
  });

  const { data: vehicleBrand } = useGetVehicleBrand(calcData.vehicleData.brandId);
  const { data: vehicleModel } = useGetVehicleModel(calcData.vehicleData.modelId);

  const paymentFrequency = isGapCalcResult(selectedCalcResult)
    ? selectedCalcResult.data?.options?.find(
        (option) =>
          option.complicityReinsurance === formData.generalData.gapComplicityReinsurance &&
          option.paymentFrequency === formData.generalData.gapPaymentFrequency,
      )?.paymentFrequency
    : calcData.generalData.paymentFrequency;

  const annualPremium = isGapCalcResult(selectedCalcResult)
    ? selectedCalcResult.data?.options?.find(
        (option) =>
          option.complicityReinsurance === formData.generalData.gapComplicityReinsurance &&
          option.paymentFrequency === formData.generalData.gapPaymentFrequency,
      )?.annualPremium
    : selectedCalcResult.data?.annualPremium;

  return (
    <div>
      <Row>
        <Col flex={1}>
          <Statistic
            className="statistic-small"
            title={t("common.insuranceInstitution")}
            value={selectedCalcResult.insuranceInstitution.name}
          />
        </Col>

        {selectedCalcResult.coverage ? (
          <Col flex={1}>
            <Statistic
              className="statistic-small"
              title={<span style={{ textTransform: "capitalize" }}>{t("calc.vehicle.sections.coverage")}</span>}
              value={selectedCalcResult.coverage}
            />
          </Col>
        ) : undefined}

        <Col flex={4}>
          <Statistic
            className="statistic-small"
            title={t("calc.helpers.summaryData.product")}
            value={t("calc.enums.calcType." + selectedCalcResult.calcType).toUpperCase()}
          />
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col flex={1}>
          <Divider titlePlacement="start">{t("calc.vehicle.attrs.clientsData._label")}</Divider>
          <ClientsListView
            clientsData={createClientsData([
              {
                client: policyHolderClient as Client,
                type: ClientFormType.POLICY_HOLDER,
              },
              {
                client: holderClient as Client,
                type: ClientFormType.HOLDER,
              },
              {
                client: ownerClient as Client,
                type: ClientFormType.OWNER,
              },
            ])}
          />
        </Col>
      </Row>
      <Row>
        <Col flex={1}>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.attrs.clientsData.policyHolderPhone")}</Col>
            <Col span={SPAN_VALUE}>{formatPhoneNumber(formData.clientsData.policyHolderPhone)}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.vehicle.attrs.clientsData.policyHolderEmail")}</Col>
            <Col span={SPAN_VALUE}>{formData.clientsData.policyHolderEmail}</Col>
          </Row>
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col flex={1}>
          <Divider titlePlacement="start">{t("contract.attrs.insurances.vehicle._label")}</Divider>
          {vehicleBrand ? renderVehicleData(calcData.vehicleData, vehicleBrand.name, vehicleModel?.name) : undefined}
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col span={11}>
          <Divider titlePlacement="start">{t("calc.summary.sections.insuranceAndCoverages")}</Divider>
          {renderVehicleInsuranceData(selectedCalcResult, {
            gapDuration: calcData.generalData.gapDuration,
            gapComplicityReinsurance: formData.generalData?.gapComplicityReinsurance,
            gapCoverageLimit: calcData.generalData.gapCoverageLimit,
          })}
        </Col>

        <Col span={1} />

        <Col span={12}>
          <Divider titlePlacement="start">{t("calc.vehicle.results.additionalInfo")}</Divider>
          <Row>
            <Col span={11}>{t("calc.vehicle.attrs.generalData.effectiveBeginningDate")}</Col>
            <Col span={13}>{formatLocaleDate(calcData.generalData.effectiveBeginningDate)}</Col>
          </Row>
          <Row>
            <Col span={11}>
              {formData.generalData?.gapPaymentFrequency === PaymentFrequency.ONCE
                ? t("calc.vehicle.results.annualPremium")
                : t("contract.attrs.annualPremium")}
            </Col>
            <Col span={13}>{isDefined(annualPremium) ? formatLocaleCurrency(annualPremium) : undefined}</Col>
          </Row>

          <Row>
            <Col span={11}>{t("contract.enums.paymentFrequency._label")}</Col>
            <Col span={13}>{t("contract.enums.paymentFrequency." + paymentFrequency)}</Col>
          </Row>
        </Col>
      </Row>

      <div className="margin-top-small">
        <GainersCommissionRateSection
          divideCommission={divideCommission}
          setDivideCommission={setDivideCommission}
          gainersDataForm={gainersDataForm}
        />
      </div>
    </div>
  );
};

export default VehicleCalcSummaryView;
